/**
 * Multi-device Lobby Screens — New Model.
 * Host: creates session, manages players + device roles.
 * Joiner: connects as a device (Reader or Scoreboard), sees read-only view.
 */

import * as ws from '../ws-client';
import { SyncedGameState } from '../../shared/protocol';
import { navigate } from '../router';
import { t } from '../i18n';
import { generateJoinQrCode, getJoinUrl } from '../qr-code';
import { setInitialState } from './multi-gameplay';

let currentState: SyncedGameState | null = null;
let unsubState: (() => void) | null = null;
let unsubEvents: (() => void) | null = null;

// --- Host: Create Game Flow ---

export function renderMultiNewGame(): void {
  const app = document.getElementById('app')!;

  app.innerHTML = `
    <h1>${t('newGame')}</h1>
    <p class="subtitle">${t('enterNameToHost')}</p>

    <div class="stack">
      <input type="text" id="host-name-input" class="input"
        maxlength="20" placeholder="${t('yourNamePlaceholder')}" autofocus />
      <p id="host-error" class="error-text" style="min-height: 1.2em;"></p>
      <button class="btn btn-primary" id="btn-create">${t('createGame')}</button>
      <button class="btn btn-tertiary" id="btn-back">${t('back')}</button>
    </div>
  `;

  const input = document.getElementById('host-name-input') as HTMLInputElement;

  document.getElementById('btn-create')?.addEventListener('click', () => createGame(input));
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter') createGame(input); });
  document.getElementById('btn-back')?.addEventListener('click', () => navigate('menu'));
}

async function createGame(input: HTMLInputElement): Promise<void> {
  const name = input.value.trim();
  if (!name || name.length > 20) {
    document.getElementById('host-error')!.textContent = t('nameTooLong');
    return;
  }

  try {
    await ws.connect();
  } catch {
    document.getElementById('host-error')!.textContent = t('cannotConnect');
    return;
  }

  const unsub = ws.onServerEvent((msg) => {
    if (msg.type === 'session-created') {
      unsub();
      setupLobbyListeners();
      renderHostLobby();
    } else if (msg.type === 'error') {
      document.getElementById('host-error')!.textContent = msg.message;
    }
  });

  ws.createSession(name);
}

// --- Joiner: Join Game Flow ---

export function renderMultiJoinGame(): void {
  const app = document.getElementById('app')!;

  app.innerHTML = `
    <h1>${t('joinGameTitle')}</h1>
    <p class="subtitle">${t('enterCodeSubtitle')}</p>

    <div class="stack">
      <div class="row" style="justify-content: center; gap: var(--space-sm);">
        ${[0, 1, 2, 3].map((i) => `
          <input type="text" maxlength="1" inputmode="numeric" pattern="[0-9]"
            class="input code-digit" data-index="${i}"
            style="width: 3.5rem; height: 4rem;
              font-size: var(--font-size-xl); padding: 0;" />
        `).join('')}
      </div>

      <p id="join-error" class="error-text" style="min-height: 1.2em;"></p>

      <button class="btn btn-primary" id="btn-join-reader">${t('joinAsReader')}</button>
      <button class="btn btn-secondary" id="btn-join-scoreboard">${t('joinAsScoreboard')}</button>
      <button class="btn btn-tertiary" id="btn-back">${t('back')}</button>
    </div>
  `;

  // Auto-advance focus between digit inputs
  document.querySelectorAll('.code-digit').forEach((inp) => {
    (inp as HTMLInputElement).addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement;
      if (target.value && target.dataset.index !== '3') {
        const next = document.querySelector(
          `.code-digit[data-index="${Number(target.dataset.index) + 1}"]`
        ) as HTMLInputElement;
        next?.focus();
      }
    });
    (inp as HTMLInputElement).addEventListener('keydown', (e) => {
      const target = e.target as HTMLInputElement;
      if (e.key === 'Backspace' && !target.value && target.dataset.index !== '0') {
        const prev = document.querySelector(
          `.code-digit[data-index="${Number(target.dataset.index) - 1}"]`
        ) as HTMLInputElement;
        prev?.focus();
      }
    });
  });

  document.getElementById('btn-join-reader')?.addEventListener('click', () => attemptJoin('reader'));
  document.getElementById('btn-join-scoreboard')?.addEventListener('click', () => attemptJoin('scoreboard'));
  document.getElementById('btn-back')?.addEventListener('click', () => navigate('menu'));
}

async function attemptJoin(role: 'reader' | 'scoreboard'): Promise<void> {
  const digits = Array.from(document.querySelectorAll('.code-digit'))
    .map((el) => (el as HTMLInputElement).value)
    .join('');
  const errorEl = document.getElementById('join-error')!;

  if (digits.length !== 4 || !/^\d{4}$/.test(digits)) {
    errorEl.textContent = t('enterValidCode');
    return;
  }

  try {
    await ws.connect();
  } catch {
    errorEl.textContent = t('cannotConnect');
    return;
  }

  const unsub = ws.onServerEvent((msg) => {
    if (msg.type === 'state-update') {
      unsub();
      setupLobbyListeners(); // Must be called BEFORE setting currentState (it calls cleanupListeners which nulls it)
      currentState = msg.state;
      renderJoinerLobby();
    } else if (msg.type === 'error') {
      errorEl.textContent = msg.message;
    }
  });

  ws.joinSession(digits, role);
}

// --- Host Lobby ---

function renderHostLobby(): void {
  const app = document.getElementById('app')!;
  const code = ws.getSessionCode();

  if (!currentState) {
    app.innerHTML = `<p class="text-muted center">${t('loading')}</p>`;
    return;
  }

  const state = currentState;
  const teamA = state.players.filter((p) => p.team === 'A');
  const teamB = state.players.filter((p) => p.team === 'B');
  const unassigned = state.players.filter((p) => p.team === null);

  app.innerHTML = `
    <div class="stack">
      <h1 style="font-size: var(--font-size-xl);">${t('gameLobby')}</h1>

      <!-- Code Display -->
      <div class="surface-elevated center">
        <p class="text-muted" style="font-size: var(--font-size-xs);">${t('shareThisCode')}</p>
        <div style="font-size: var(--font-size-2xl); font-weight: var(--font-weight-bold);
          letter-spacing: 0.3em; color: var(--color-accent); margin: var(--space-sm) 0;">
          ${code}
        </div>
        <div id="qr-code-container" style="margin-top: var(--space-sm); max-width: 160px; margin-left: auto; margin-right: auto;">
          ${generateJoinQrCode(code || '')}
        </div>
        <p class="text-muted" style="font-size: var(--font-size-xs); margin-top: var(--space-xs);">
          ${t('scanToJoin') || 'Scan to join'}
        </p>
      </div>

      <!-- Connected Devices -->
      <div class="surface" style="padding: var(--space-md);">
        <p class="setting-label" style="margin-bottom: var(--space-sm);">${t('connectedDevices')}</p>
        <div class="stack-sm">
          ${state.devices.map((d) => `
            <div class="row" style="justify-content: space-between; align-items: center;">
              <span>${d.label}${d.isHost ? ` (${t('you')})` : ''}</span>
              <span class="badge">${d.role}</span>
              ${!d.isHost ? `
                <div style="display: flex; gap: var(--space-xs);">
                  <button class="pill-assign-a" data-conn-id="${d.connectionId}" data-role="reader"
                    style="font-size: var(--font-size-xs); padding: 2px 8px;"
                    ${d.role === 'reader' ? 'disabled' : ''}>📱 Reader</button>
                  <button class="pill-assign-b" data-conn-id="${d.connectionId}" data-role="scoreboard"
                    style="font-size: var(--font-size-xs); padding: 2px 8px;"
                    ${d.role === 'scoreboard' ? 'disabled' : ''}>📊 Scoreboard</button>
                </div>
              ` : ''}
            </div>
          `).join('')}
        </div>
      </div>

      <!-- Add Player -->
      <div class="surface" style="padding: var(--space-md);">
        <p class="setting-label" style="margin-bottom: var(--space-sm);">${t('addPlayer')}</p>
        <div class="row" style="gap: var(--space-sm);">
          <input type="text" id="player-name-input" class="input"
            maxlength="20" placeholder="${t('playerNamePlaceholder')}"
            style="flex: 1;" />
          <button class="btn btn-primary" id="btn-add-player"
            style="width: auto; padding: var(--btn-padding-y) var(--space-lg);">${t('add')}</button>
        </div>
        <p id="add-player-error" class="error-text" style="min-height: 1em; font-size: var(--font-size-xs);"></p>
      </div>

      <!-- Unassigned Players -->
      ${unassigned.length > 0 ? `
        <div class="surface" style="padding: var(--space-md);">
          <p class="text-muted" style="font-size: var(--font-size-xs); margin-bottom: var(--space-sm);">
            ${t('unassignedTapToAssign')}
          </p>
          <div class="player-pills">
            ${unassigned.map((p) => `
              <div class="player-pill" data-player-id="${p.id}">
                <span class="pill-name">${p.displayName}</span>
                <button class="pill-team-a" data-assign-id="${p.id}" title="${t('teamA')}">A</button>
                <button class="pill-team-b" data-assign-id="${p.id}" title="${t('teamB')}">B</button>
              </div>
            `).join('')}
          </div>
        </div>
      ` : ''}

      <!-- Teams -->
      <div class="teams-grid">
        <div class="team-column">
          <h3 style="color: var(--color-accent);">${t('teamA')}</h3>
          <p class="text-muted" style="font-size: var(--font-size-xs);">
            ${teamA.length} ${teamA.length !== 1 ? t('players') : t('player')}
          </p>
          <div class="player-pills">
            ${teamA.map((p) => `
              <div class="player-pill">
                <span class="pill-name">${p.displayName}${p.isHost ? ` <span class="badge">${t('host')}</span>` : ''}</span>
                <button class="pill-move-btn" data-move-id="${p.id}" data-move-to="B" title="→B">→B</button>
              </div>
            `).join('')}
          </div>
        </div>

        <div class="team-column">
          <h3 style="color: var(--color-accent);">${t('teamB')}</h3>
          <p class="text-muted" style="font-size: var(--font-size-xs);">
            ${teamB.length} ${teamB.length !== 1 ? t('players') : t('player')}
          </p>
          <div class="player-pills">
            ${teamB.map((p) => `
              <div class="player-pill">
                <span class="pill-name">${p.displayName}</span>
                <button class="pill-move-btn" data-move-id="${p.id}" data-move-to="A" title="→A">→A</button>
              </div>
            `).join('')}
          </div>
        </div>
      </div>

      <!-- Start -->
      <button class="btn btn-primary" id="btn-start-game">${t('startGame')}</button>
      <p id="lobby-error" class="error-text" style="min-height: 1.2em;"></p>
      <button class="btn btn-tertiary" id="btn-leave">${t('leaveGame')}</button>
    </div>
  `;

  // Event listeners
  bindHostLobbyEvents();
}

function bindHostLobbyEvents(): void {
  // Add player
  const playerInput = document.getElementById('player-name-input') as HTMLInputElement;
  const addPlayerBtn = document.getElementById('btn-add-player');
  const addPlayerError = document.getElementById('add-player-error');

  const handleAddPlayer = () => {
    const name = playerInput?.value.trim();
    if (!name) return;
    if (name.length > 20) {
      if (addPlayerError) addPlayerError.textContent = t('maxChars');
      return;
    }
    ws.addPlayer(name, null);
    if (playerInput) playerInput.value = '';
    if (addPlayerError) addPlayerError.textContent = '';
  };

  addPlayerBtn?.addEventListener('click', handleAddPlayer);
  playerInput?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') handleAddPlayer();
  });

  // Assign device roles
  document.querySelectorAll('[data-conn-id]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const connId = (btn as HTMLElement).dataset.connId!;
      const role = (btn as HTMLElement).dataset.role as 'reader' | 'scoreboard';
      ws.assignDeviceRole(connId, role);
    });
  });

  // Assign team buttons (unassigned)
  document.querySelectorAll('.pill-team-a').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = (btn as HTMLElement).dataset.assignId!;
      ws.assignTeam(id, 'A');
    });
  });
  document.querySelectorAll('.pill-team-b').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = (btn as HTMLElement).dataset.assignId!;
      ws.assignTeam(id, 'B');
    });
  });

  // Move buttons
  document.querySelectorAll('.pill-move-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = (btn as HTMLElement).dataset.moveId!;
      const team = (btn as HTMLElement).dataset.moveTo as 'A' | 'B';
      ws.assignTeam(id, team);
    });
  });

  // Start game
  document.getElementById('btn-start-game')?.addEventListener('click', () => {
    ws.startSetup();
  });

  // Leave
  document.getElementById('btn-leave')?.addEventListener('click', () => {
    cleanupListeners();
    ws.leaveSession();
    navigate('menu');
  });
}

// --- Joiner Lobby (Read-only) ---

function renderJoinerLobby(): void {
  const app = document.getElementById('app')!;
  const code = ws.getSessionCode();

  if (!currentState) {
    app.innerHTML = `<p class="text-muted center">${t('connecting')}</p>`;
    return;
  }

  const state = currentState;
  const myRole = state.myDeviceRole;
  const teamA = state.players.filter((p) => p.team === 'A');
  const teamB = state.players.filter((p) => p.team === 'B');

  app.innerHTML = `
    <div class="stack">
      <h1 style="font-size: var(--font-size-xl);">${t('gameLobby')} ${code}</h1>
      <p class="subtitle">${t('waitingForHostToStart')}</p>

      <div class="surface center" style="padding: var(--space-md);">
        <p class="text-muted" style="font-size: var(--font-size-xs);">${t('yourRole')}</p>
        <p style="font-size: var(--font-size-lg); font-weight: var(--font-weight-bold); color: var(--color-accent);">
          ${myRole === 'reader' ? '📱 Reader' : '📊 Scoreboard'}
        </p>
      </div>

      <!-- Teams (read-only) -->
      <div class="teams-grid">
        <div class="team-column">
          <h3 style="color: var(--color-accent);">${t('teamA')}</h3>
          <div class="player-pills">
            ${teamA.map((p) => `
              <div class="player-pill">
                <span class="pill-name">${p.displayName}${p.isHost ? ` <span class="badge">${t('host')}</span>` : ''}</span>
              </div>
            `).join('')}
          </div>
        </div>

        <div class="team-column">
          <h3 style="color: var(--color-accent);">${t('teamB')}</h3>
          <div class="player-pills">
            ${teamB.map((p) => `
              <div class="player-pill">
                <span class="pill-name">${p.displayName}</span>
              </div>
            `).join('')}
          </div>
        </div>
      </div>

      <button class="btn btn-tertiary" id="btn-leave">${t('leaveGame')}</button>
    </div>
  `;

  document.getElementById('btn-leave')?.addEventListener('click', () => {
    cleanupListeners();
    ws.leaveSession();
    navigate('menu');
  });
}

// --- State & Event Listeners ---

function setupLobbyListeners(): void {
  cleanupListeners();

  unsubState = ws.onStateUpdate((state) => {
    currentState = state;
    handlePhaseChange(state);
  });

  unsubEvents = ws.onServerEvent((msg) => {
    if (msg.type === 'error') {
      const errorEl = document.getElementById('lobby-error')
        || document.getElementById('add-player-error')
        || document.getElementById('host-error');
      if (errorEl) errorEl.textContent = msg.message;
    }
  });

  // Listen for locale-changed re-render requests (safe re-render without losing session)
  window.addEventListener('multi-rerender', handleMultiRerender);
}

function handleMultiRerender(): void {
  if (currentState) {
    if (currentState.phase === 'lobby') {
      if (currentState.isHost) renderHostLobby();
      else renderJoinerLobby();
    }
    // Other phases are handled by multi-gameplay.ts
  }
}

function handlePhaseChange(state: SyncedGameState): void {
  switch (state.phase) {
    case 'lobby':
      if (state.isHost) renderHostLobby();
      else renderJoinerLobby();
      break;
    case 'setup':
    case 'deck-creation':
      cleanupListeners();
      setInitialState(state);
      navigate('multi-setup');
      break;
    case 'round-interstitial':
      cleanupListeners();
      setInitialState(state);
      navigate('multi-round-interstitial');
      break;
    case 'turn-waiting':
      cleanupListeners();
      setInitialState(state);
      navigate('multi-turn-waiting');
      break;
    case 'turn-active':
      cleanupListeners();
      setInitialState(state);
      navigate('multi-turn-active');
      break;
    case 'turn-end':
      cleanupListeners();
      setInitialState(state);
      navigate('multi-turn-end');
      break;
    case 'round-complete':
      cleanupListeners();
      setInitialState(state);
      navigate('multi-round-complete');
      break;
    case 'game-end':
      cleanupListeners();
      setInitialState(state);
      navigate('multi-game-end');
      break;
  }
}

function cleanupListeners(): void {
  if (unsubState) { unsubState(); unsubState = null; }
  if (unsubEvents) { unsubEvents(); unsubEvents = null; }
  window.removeEventListener('multi-rerender', handleMultiRerender);
  currentState = null;
}
